import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { login, parseAuthMeProfile } from "./api/authApi";
import { sanitizeApiMessage } from "./auth/clientAuth";
import WillPlayMotionLogo from "./components/WillPlayMotionLogo";
import PasswordInput from "./PasswordInput";

/** @param {{ onLoginSuccess: (profile: { username: string, email?: string, userId?: number | null } | null) => void }} props */
function Login({ onLoginSuccess }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [registrationNote] = useState(() =>
    typeof location.state?.registrationNote === "string"
      ? location.state.registrationNote
      : ""
  );

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const { response, data } = await login({
        usernameOrEmail: usernameOrEmail.trim(),
        password
      });

      if (!response.ok) {
        const raw =
          (data && typeof data === "object" && typeof data.message === "string" && data.message) ||
          (typeof data === "string" && data) ||
          "Login failed. Check your credentials.";
        setError(sanitizeApiMessage(raw));
        return;
      }

      const profile =
        data && typeof data === "object" ? parseAuthMeProfile(data) : null;

      onLoginSuccess(profile);
      const from = location.state?.from;
      const target =
        typeof from === "string" && from.startsWith("/") && !from.startsWith("//")
          ? from
          : "/";
      navigate(target, { replace: true });
    } catch (apiError) {
      console.error(apiError);
      const msg =
        typeof navigator !== "undefined" && navigator.onLine === false
          ? "You appear to be offline. Check your connection."
          : "Cannot reach the server. Confirm the API is running and REACT_APP_API_BASE_URL is correct.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="auth-page">
      <form className="auth-card" onSubmit={handleLogin}>
        <div className="auth-brand">
          <WillPlayMotionLogo size={30} className="auth-brand-logo" />
          <span>WillPlay</span>
        </div>
        <h1>Login</h1>
        <p className="auth-step-hint">Sign in with your WillPlay email or username and password.</p>

        {registrationNote ? (
          <p className="success-text" role="status">
            {registrationNote}
          </p>
        ) : null}

        <input
          type="text"
          placeholder="Email or username"
          value={usernameOrEmail}
          onChange={(e) => setUsernameOrEmail(e.target.value)}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
        />

        <PasswordInput
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
          name="password"
        />

        <p className="auth-forgot-row">
          <Link to="/forgot-password" className="auth-forgot-link">
            Forgot password?
          </Link>
        </p>

        {error ? <p className="error-text">{error}</p> : null}

        <button className="primary-btn" type="submit" disabled={loading}>
          {loading ? "Signing in..." : "Login"}
        </button>

        <p>
          New to WillPlay? <Link to="/register">Register</Link>
        </p>
      </form>
    </main>
  );
}

export default Login;